import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface EmailRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailInput: string;
  onEmailInputChange: (email: string) => void;
  onSubmit: () => void;
  toolId: string;
  lang?: string;
}

export const EmailRequestDialog: React.FC<EmailRequestDialogProps> = ({
  open,
  onOpenChange,
  emailInput,
  onEmailInputChange,
  onSubmit,
  toolId,
  lang = 'en'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefix = `/${lang}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-xl shadow-card">
        <DialogHeader className="space-y-1">
          <DialogTitle>{t('survey.emailDialog.header')}</DialogTitle>
          <DialogDescription>
            {t('survey.emailDialog.body')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-input">{t('survey.emailDialog.placeholder')}</Label>
            <Input
              id="email-input"
              placeholder={t('survey.emailDialog.placeholder') as string}
              type="email"
              value={emailInput}
              onChange={(e) => onEmailInputChange(e.target.value)}
            />
          </div>

          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground/80">{t('survey.emailDialog.noSpam')}</p>
            <p className="mt-1">{t('survey.emailDialog.useFor')}</p>
            <ul className="mt-1 space-y-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5" aria-hidden="true" />
                <span>{t('survey.emailDialog.bullet1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5" aria-hidden="true" />
                <span>{t('survey.emailDialog.bullet2')}</span>
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t('survey.emailDialog.back')}</Button>
            <Button onClick={onSubmit}>{t('survey.emailDialog.continue')}</Button>
          </div>

          {/* Create Account Section - Highlighted */}
          <div className="rounded-lg border p-4 bg-primary/5 border-primary/20">
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('survey.emailDialog.ctaCreateAccountTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('survey.emailDialog.ctaCreateAccountSubtext')}</p>
              <Button variant="secondary" className="mt-2" onClick={() => navigate(`${prefix}/auth`)}>
                {t('survey.emailDialog.ctaCreateAccountButton')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 